/* eslint-disable */
/**
 * A lightweight youtube embed. Still should feel the same to the user, just MUCH faster to initialize and paint.
 *
 * Thx to these as the inspiration
 *   https://storage.googleapis.com/amp-vs-non-amp/youtube-lazy.html
 *   https://autoplay-youtube-player.glitch.me/
 *
 * Once built it, I also found these:
 *   https://github.com/ampproject/amphtml/blob/master/extensions/amp-youtube (👍👍)
 *   https://github.com/Daugilas/lazyYT
 *   https://github.com/vb/lazyframe
 */
if (!customElements.get('lite-youtube')) {
  class LiteYTEmbed extends HTMLElement {
    connectedCallback() {
      this.videoId = this.getAttribute('videoid');

      let playBtnEl = this.querySelector('.lty-playbtn');
      // A label for the button takes priority over a [playlabel] attribute on the custom-element
      this.playLabel = (playBtnEl && playBtnEl.textContent.trim()) || this.getAttribute('playlabel') || 'Play';

      /**
           * Lo, the youtube placeholder image!  (aka the thumbnail, poster image, etc)
           *
           * See https://github.com/paulirish/lite-youtube-embed/blob/master/youtube-thumbnail-urls.md
           */
      if (!this.style.backgroundImage) {
        // Prefer a content-editor-specified bgImage (read from `data-bg-image` attribute set by
        // _11ty/filters/utilities/embeds.js — CSP style-src-attr cleanup, replaces the prior
        // inline `style="background-image:url(...)"`). Fall back to the default YouTube
        // thumbnail when no custom bgImage was provided.
        const customBgImage = this.dataset.bgImage;
        if (customBgImage) {
          this.style.backgroundImage = `url("${customBgImage}")`;
        } else {
          // hqdefault.jpg is always available, including on old videos. Upgrade to a
          // higher-res poster when YouTube has one (see upgradePosterImage).
          this.style.backgroundImage = `url("https://i.ytimg.com/vi/${this.videoId}/hqdefault.jpg")`;
          this.upgradePosterImage();
        }
      }

      // Set up play button, and its visually hidden label
      if (!playBtnEl) {
        playBtnEl = document.createElement('button');
        playBtnEl.type = 'button';
        playBtnEl.classList.add('lty-playbtn');
        this.append(playBtnEl);
      }
      if (!playBtnEl.textContent) {
        const playBtnLabelEl = document.createElement('span');
        playBtnLabelEl.className = 'is-visually-hidden';
        playBtnLabelEl.textContent = this.playLabel;
        playBtnEl.append(playBtnLabelEl);
      }
      // playBtnEl.removeAttribute('href');
      // preventDefault instead of removing href for seo reasons
      playBtnEl.addEventListener('click', (e) => e.preventDefault());

      // On hover (or tap), warm up the TCP connections we're (likely) about to use.
      this.addEventListener('pointerover', LiteYTEmbed.warmConnections, {once: true});

      // Once the user clicks, add the real iframe and drop our play button
      this.addEventListener('click', this.addIframe);

      // Chrome & Edge desktop have no problem with the basic YouTube Embed with ?autoplay=1
      // However Safari desktop and most/all mobile browsers do not successfully track the user gesture of clicking through the creation/loading of the iframe,
      // so they don't autoplay automatically. Instead we must load an additional 2 sequential JS files (1KB + 165KB) (un-br) for the YT Player API
      this.needsYTApiForAutoplay = navigator.vendor.includes('Apple') || navigator.userAgent.includes('Mobi');
    }

    /**
       * Add a <link rel={preload | preconnect} ...> to the head
       */
    static addPrefetch(kind, url, as) {
      const linkEl = document.createElement('link');
      linkEl.rel = kind;
      linkEl.href = url;
      if (as) {
        linkEl.as = as;
      }
      document.head.append(linkEl);
    }

    /**
       * Begin pre-connecting to warm up the iframe load
       * Since the embed's network requests load within its iframe,
       *   preload/prefetch'ing them outside the iframe will only cause double-downloads.
       * So, the best we can do is warm up a few connections to origins that are in the critical path.
       *
       * Maybe `<link rel=preload as=document>` would work, but it's unsupported: http://crbug.com/593267
       * But TBH, I don't think it'll happen soon with Site Isolation and split caches adding serious complexity.
       */
    static warmConnections() {
      if (LiteYTEmbed.preconnected) return;

      // The iframe document and most of its subresources come right off youtube.com
      LiteYTEmbed.addPrefetch('preconnect', 'https://www.youtube-nocookie.com');
      // The botguard script is fetched off from google.com
      LiteYTEmbed.addPrefetch('preconnect', 'https://www.google.com');

      // Not certain if these ad related domains are in the critical path. Could verify with domain-specific throttling.
      LiteYTEmbed.addPrefetch('preconnect', 'https://googleads.g.doubleclick.net');
      LiteYTEmbed.addPrefetch('preconnect', 'https://static.doubleclick.net');

      LiteYTEmbed.preconnected = true;
    }

    fetchYTPlayerApi() {
      if (window.YT || (window.YT && window.YT.Player)) return;

      this.ytApiPromise = new Promise((res, rej) => {
        const el = document.createElement('script');
        el.src = 'https://www.youtube.com/iframe_api';
        el.async = true;
        el.addEventListener('load', () => {
          YT.ready(res);
        });
        el.addEventListener('error', rej);
        this.append(el);
      });
    }

    async addYTPlayerIframe(params) {
      this.fetchYTPlayerApi();
      await this.ytApiPromise;

      const videoPlaceholderEl = document.createElement('div');
      this.append(videoPlaceholderEl);

      const paramsObj = Object.fromEntries(params.entries());

      new YT.Player(videoPlaceholderEl, {
        width: '100%',
        videoId: this.videoId,
        playerVars: paramsObj,
        events: {
          'onReady': (event) => {
            event.target.playVideo();
          },
        },
      });
    }

    async addIframe() {
      if (this.classList.contains('is-activated')) return;
      this.classList.add('is-activated');

      const params = new URLSearchParams(this.getAttribute('params') || []);
      params.append('autoplay', '1');
      params.append('playsinline', '1');

      if (this.needsYTApiForAutoplay) {
        return this.addYTPlayerIframe(params);
      }

      const iframeEl = document.createElement('iframe');
      iframeEl.width = 560;
      iframeEl.height = 315;
      // No encoding necessary as [title] is safe. https://cheatsheetseries.owasp.org/cheatsheets/Cross_Site_Scripting_Prevention_Cheat_Sheet.html#:~:text=Safe%20HTML%20Attributes%20include
      iframeEl.title = this.playLabel;
      iframeEl.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture';
      iframeEl.allowFullscreen = true;
      // AFAIK, the encoding here isn't necessary for XSS, but we'll do it only because this is a URL
      // https://stackoverflow.com/q/64959723/89484
      iframeEl.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(this.videoId)}?${params.toString()}`;
      this.append(iframeEl);

      // Set focus for a11y
      iframeEl.focus();
    }

    /**
     * Paint hqdefault immediately, then upgrade to the highest-quality poster YouTube
     * actually has for this video. Missing sizes still return a 120x90 JPEG, so we cannot
     * rely on img.onerror — see youtube-thumbnail-urls.md.
     */
    upgradePosterImage() {
      // Defer to reduce network contention with first paint / LCP.
      setTimeout(() => {
        const candidates = [
          `https://i.ytimg.com/vi_webp/${this.videoId}/maxresdefault.webp`,
          `https://i.ytimg.com/vi/${this.videoId}/maxresdefault.jpg`,
          `https://i.ytimg.com/vi_webp/${this.videoId}/sddefault.webp`,
          `https://i.ytimg.com/vi/${this.videoId}/sddefault.jpg`,
        ];
        this.tryPosterCandidates(candidates, 0);
      }, 100);
    }

    tryPosterCandidates(urls, index) {
      if (index >= urls.length) return;

      const url = urls[index];
      const img = new Image();
      img.fetchPriority = 'low';
      img.referrerPolicy = 'origin';
      img.onload = (e) => {
        const isPlaceholder = e.target.naturalHeight === 90 && e.target.naturalWidth === 120;
        if (isPlaceholder) {
          this.tryPosterCandidates(urls, index + 1);
          return;
        }
        this.style.backgroundImage = `url("${url}")`;
      };
      img.onerror = () => {
        this.tryPosterCandidates(urls, index + 1);
      };
      img.src = url;
    }
  }
  // Register custom element
  customElements.define('lite-youtube', LiteYTEmbed);
}
